import type { StoredQuery } from "./persistence";
import type { SharedConfigPayload, SharedConfigPayloadQuery } from "./sharedTypes";
import { resolveSharedPayloadServer } from "./sharedTypes";

export interface QueryDraftSnapshot {
  queryText: string;
  variables: string;
  headers: string;
  remarks?: string;
  riskLevel?: StoredQuery["riskLevel"];
  requestType?: StoredQuery["requestType"];
  operationConfig?: StoredQuery["operationConfig"];
  restMethod?: StoredQuery["restMethod"];
  restPath?: string;
  restParams?: StoredQuery["restParams"];
  restContentType?: StoredQuery["restContentType"];
}

export interface SharedPayloadMergeConflict {
  queryId: string;
  remoteDeleted: boolean;
  remoteQuery?: SharedConfigPayloadQuery;
}

export interface SharedPayloadMergeResult {
  mergedServer: SharedConfigPayload["server"];
  mergedQueries: SharedConfigPayloadQuery[];
  changedQueryIds: string[];
  deletedQueryIds: string[];
  conflictQueryIds: string[];
  conflicts: SharedPayloadMergeConflict[];
  serverConflict: boolean;
}

export function toQueryDraftSnapshot(
  input: Pick<
    StoredQuery,
    "queryText" | "variables" | "headers" | "remarks" | "riskLevel" | "requestType" | "operationConfig" | "restMethod" | "restPath" | "restParams" | "restContentType"
  >,
): QueryDraftSnapshot {
  return {
    queryText: input.queryText,
    variables: input.variables,
    headers: input.headers,
    remarks: input.remarks ?? "",
    riskLevel: input.riskLevel ?? "normal",
    requestType: input.requestType ?? "graphql",
    ...(input.operationConfig ? { operationConfig: input.operationConfig } : {}),
    restMethod: input.restMethod ?? "GET",
    restPath: input.restPath ?? "/",
    restParams: input.restParams ?? [],
    restContentType: input.restContentType ?? "json",
  };
}

export function storedQueryToPayloadQuery(query: StoredQuery): SharedConfigPayloadQuery {
  return {
    id: query.id,
    name: query.name,
    queryText: query.queryText,
    variables: query.variables,
    headers: query.headers,
    ...(query.remarks ? { remarks: query.remarks } : {}),
    ...(query.riskLevel ? { riskLevel: query.riskLevel } : {}),
    ...(query.category ? { category: query.category } : {}),
    ...(query.pinnedAt !== undefined ? { pinnedAt: query.pinnedAt } : {}),
    ...(query.requestType ? { requestType: query.requestType } : {}),
    ...(query.operationConfig ? { operationConfig: query.operationConfig } : {}),
    ...(query.restMethod ? { restMethod: query.restMethod } : {}),
    ...(query.restPath != null ? { restPath: query.restPath } : {}),
    ...(query.restParams ? { restParams: query.restParams } : {}),
    ...(query.restContentType ? { restContentType: query.restContentType } : {}),
  };
}

export function payloadQueryToDraft(query: SharedConfigPayloadQuery): QueryDraftSnapshot {
  return {
    queryText: query.queryText,
    variables: query.variables,
    headers: query.headers,
    remarks: query.remarks ?? "",
    riskLevel: query.riskLevel ?? "normal",
    requestType: query.requestType ?? "graphql",
    ...(query.operationConfig ? { operationConfig: query.operationConfig } : {}),
    restMethod: query.restMethod ?? "GET",
    restPath: query.restPath ?? "/",
    restParams: query.restParams ?? [],
    restContentType: query.restContentType ?? "json",
  };
}

export function applyDraftToPayloadQuery(
  query: SharedConfigPayloadQuery,
  draft: QueryDraftSnapshot,
): SharedConfigPayloadQuery {
  return {
    ...query,
    queryText: draft.queryText,
    variables: draft.variables,
    headers: draft.headers,
    remarks: draft.remarks ?? "",
    riskLevel: draft.riskLevel ?? "normal",
    requestType: draft.requestType ?? "graphql",
    ...(draft.operationConfig ? { operationConfig: draft.operationConfig } : {}),
    restMethod: draft.restMethod ?? "GET",
    restPath: draft.restPath ?? "/",
    restParams: draft.restParams ?? [],
    restContentType: draft.restContentType ?? "json",
  };
}

export function queryDraftsEqual(a: QueryDraftSnapshot, b: QueryDraftSnapshot): boolean {
  return a.queryText === b.queryText
    && a.variables === b.variables
    && a.headers === b.headers
    && (a.remarks ?? "") === (b.remarks ?? "")
    && (a.riskLevel ?? "normal") === (b.riskLevel ?? "normal")
    && (a.requestType ?? "graphql") === (b.requestType ?? "graphql")
    && JSON.stringify(a.operationConfig ?? null) === JSON.stringify(b.operationConfig ?? null)
    && (a.restMethod ?? "GET") === (b.restMethod ?? "GET")
    && (a.restPath ?? "/") === (b.restPath ?? "/")
    && JSON.stringify(a.restParams ?? []) === JSON.stringify(b.restParams ?? [])
    && (a.restContentType ?? "json") === (b.restContentType ?? "json");
}

export type QuerySyncAdoptionAction = "none" | "advanceBase" | "applySynced";

export function getQuerySyncAdoptionAction(input: {
  baseSnapshot: QueryDraftSnapshot | null;
  editorSnapshot: QueryDraftSnapshot;
  syncedSnapshot: QueryDraftSnapshot | null;
}): QuerySyncAdoptionAction {
  const { baseSnapshot, editorSnapshot, syncedSnapshot } = input;
  if (!baseSnapshot || !syncedSnapshot) return "none";
  if (queryDraftsEqual(baseSnapshot, syncedSnapshot)) return "none";
  if (queryDraftsEqual(editorSnapshot, syncedSnapshot)) return "advanceBase";
  if (queryDraftsEqual(editorSnapshot, baseSnapshot)) return "applySynced";
  return "none";
}

export function payloadQueriesEqual(
  left: SharedConfigPayloadQuery | undefined,
  right: SharedConfigPayloadQuery | undefined,
): boolean {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return left.id === right.id
    && left.name === right.name
    && left.queryText === right.queryText
    && left.variables === right.variables
    && left.headers === right.headers
    && (left.remarks ?? "") === (right.remarks ?? "")
    && (left.riskLevel ?? "normal") === (right.riskLevel ?? "normal")
    && (left.category ?? "") === (right.category ?? "")
    && (left.pinnedAt ?? null) === (right.pinnedAt ?? null)
    && (left.requestType ?? "graphql") === (right.requestType ?? "graphql")
    && JSON.stringify(left.operationConfig ?? null) === JSON.stringify(right.operationConfig ?? null)
    && (left.restMethod ?? "GET") === (right.restMethod ?? "GET")
    && (left.restPath ?? "/") === (right.restPath ?? "/")
    && JSON.stringify(left.restParams ?? []) === JSON.stringify(right.restParams ?? [])
    && (left.restContentType ?? "json") === (right.restContentType ?? "json");
}

export function payloadServersEqual(
  left: SharedConfigPayload["server"],
  right: SharedConfigPayload["server"],
): boolean {
  const resolvedLeft = resolveSharedPayloadServer(left);
  const resolvedRight = resolveSharedPayloadServer(right);
  return resolvedLeft.name === resolvedRight.name
    && (resolvedLeft.serverKind ?? "http") === (resolvedRight.serverKind ?? "http")
    && JSON.stringify(resolvedLeft.connectionConfig ?? null) === JSON.stringify(resolvedRight.connectionConfig ?? null)
    && resolvedLeft.baseUrl === resolvedRight.baseUrl
    && (resolvedLeft.graphqlPath ?? "") === (resolvedRight.graphqlPath ?? "")
    && (resolvedLeft.restPath ?? "") === (resolvedRight.restPath ?? "")
    && resolvedLeft.authType === resolvedRight.authType
    && resolvedLeft.authConfig === resolvedRight.authConfig
    && Boolean(resolvedLeft.isProduction) === Boolean(resolvedRight.isProduction)
    && (resolvedLeft.timeoutMs ?? undefined) === (resolvedRight.timeoutMs ?? undefined);
}

export function mergeSharedPayloads(input: {
  basePayload?: SharedConfigPayload | null;
  localPayload: SharedConfigPayload;
  remotePayload: SharedConfigPayload;
}): SharedPayloadMergeResult {
  const basePayload = input.basePayload ?? null;
  const localServer = input.localPayload.server;
  const remoteServer = input.remotePayload.server;
  const baseServer = basePayload?.server;

  const localServerChanged = baseServer ? !payloadServersEqual(localServer, baseServer) : !payloadServersEqual(localServer, remoteServer);
  const remoteServerChanged = baseServer ? !payloadServersEqual(remoteServer, baseServer) : !payloadServersEqual(remoteServer, localServer);
  const serverConflict = Boolean(
    baseServer
      ? localServerChanged && remoteServerChanged && !payloadServersEqual(localServer, remoteServer)
      : localServerChanged && remoteServerChanged,
  );

  const mergedServer = serverConflict
    ? localServer
    : baseServer
      ? localServerChanged && !remoteServerChanged
        ? localServer
        : remoteServer
      : payloadServersEqual(localServer, remoteServer)
        ? localServer
        : localServer;

  const baseQueries = new Map((basePayload?.queries ?? []).map((query) => [query.id, query]));
  const localQueries = new Map(input.localPayload.queries.map((query) => [query.id, query]));
  const remoteQueries = new Map(input.remotePayload.queries.map((query) => [query.id, query]));

  const allQueryIds = [...new Set([
    ...baseQueries.keys(),
    ...localQueries.keys(),
    ...remoteQueries.keys(),
  ])].sort((a, b) => a.localeCompare(b));

  const mergedQueries: SharedConfigPayloadQuery[] = [];
  const changedQueryIds = new Set<string>();
  const deletedQueryIds = new Set<string>();
  const conflicts: SharedPayloadMergeConflict[] = [];

  for (const queryId of allQueryIds) {
    const baseQuery = baseQueries.get(queryId);
    const localQuery = localQueries.get(queryId);
    const remoteQuery = remoteQueries.get(queryId);

    if (!basePayload) {
      if (payloadQueriesEqual(localQuery, remoteQuery)) {
        if (localQuery) mergedQueries.push(localQuery);
        continue;
      }

      if (!localQuery && remoteQuery) {
        mergedQueries.push(remoteQuery);
        changedQueryIds.add(queryId);
        continue;
      }

      if (localQuery && !remoteQuery) {
        mergedQueries.push(localQuery);
        continue;
      }

      if (localQuery) {
        mergedQueries.push(localQuery);
        conflicts.push({ queryId, remoteDeleted: false, ...(remoteQuery ? { remoteQuery } : {}) });
      }
      continue;
    }

    const localChanged = !payloadQueriesEqual(localQuery, baseQuery);
    const remoteChanged = !payloadQueriesEqual(remoteQuery, baseQuery);

    if (!localChanged && !remoteChanged) {
      if (remoteQuery) mergedQueries.push(remoteQuery);
      continue;
    }

    if (localChanged && !remoteChanged) {
      if (localQuery) mergedQueries.push(localQuery);
      continue;
    }

    if (!localChanged && remoteChanged) {
      if (remoteQuery) {
        mergedQueries.push(remoteQuery);
        changedQueryIds.add(queryId);
      } else {
        deletedQueryIds.add(queryId);
      }
      continue;
    }

    if (payloadQueriesEqual(localQuery, remoteQuery)) {
      if (localQuery) mergedQueries.push(localQuery);
      continue;
    }

    if (localQuery) {
      mergedQueries.push(localQuery);
    }
    conflicts.push({ queryId, remoteDeleted: !remoteQuery, ...(remoteQuery ? { remoteQuery } : {}) });
  }

  return {
    mergedServer,
    mergedQueries,
    changedQueryIds: [...changedQueryIds],
    deletedQueryIds: [...deletedQueryIds],
    conflictQueryIds: conflicts.map((conflict) => conflict.queryId),
    conflicts,
    serverConflict,
  };
}
